import {
  Flex,
  Input,
  Modal,
  Button,
  Upload,
  message,
  Row,
  Col,
  Skeleton,
  Empty,
  Image,
  Dropdown,
  Popconfirm,
  Alert,
  Checkbox,
  Pagination,
} from "antd";
import { useEffect, useState } from "react";
import {
  RiCloseFill,
  RiImage2Fill,
  RiSearchLine,
  RiUploadCloud2Line,
  RiPencilFill,
  RiDeleteBin7Line,
} from "react-icons/ri";
import UploadUtils from "../utilities/uploadUtils";
import requests from "../utilities/api";
import endpoints from "../constants/endpoints";
import { type } from "@testing-library/user-event/dist/type";

export default function MediaPicker({ value = [], onChange, max }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [media, setMedia] = useState({
    data: [],
    per_page: 24,
    current_page: 1,
    total: 0,
  });
  const [alert, setAlert] = useState({});

  const getMedia = async ({
    page = 1,
    search = null,
  } = {}) => {
    const api = requests();
    try {
      setLoading(true);
      const response = await api.get(
        endpoints.attachment,
        {
          limit: 24,
          page: page,
          search: search,
        },
        true,
      );
      const { per_page, data, current_page, total } = response.data;
      setMedia({
        data: data,
        per_page,
        current_page,
        total,
      });
      setLoading(false);
    } catch (e) {
      message.error("Unable to load media files");
    }
  };
  useEffect(() => {
    if (isModalOpen === true) {
      getMedia({ page: 1 });
    }
  }, [isModalOpen]);

  return (
    <>
      <Flex wrap gap={8}>
        <div
          style={{
            width: "120px",
            height: "120px",
            border: "1px dashed #d9d9d9",
            borderRadius: 16,
            padding: "26px 16px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            cursor: "pointer",
            background: "rgba(26, 55, 96, 0.02)",
          }}
          onClick={() => setIsModalOpen(true)}
        >
          <RiImage2Fill fontSize={18} />
          <strong>Media</strong>
        </div>
        {value?.map((mediaItem) => {
          return (
            <div
              key={mediaItem.uid}
              style={{
                width: "120px",
                height: "120px",
                border: "1px solid #ccc",
                borderRadius: 16,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <Button
                type="primary"
                danger
                shape="circle"
                size="small"
                icon={<RiDeleteBin7Line />}
                style={{ position: "absolute", top: 4, right: 4, zIndex: 10 }}
                onClick={(e) => {
                  e.stopPropagation();
                  const updated = value.filter(
                    (img) => img.uid !== mediaItem.uid,
                  );
                  onChange(updated);
                }}
              />
              <Image
                preview={false}
                src={mediaItem?.thumbnail || mediaItem?.url}
                style={{
                  objectFit: "contain",
                  width: "100%",
                }}
                alt="selectedImage"
              />
            </div>
          );
        })}
      </Flex>
      <Modal
        destroyOnClose={true}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={false}
        okText="Select"
        closeIcon={false}
        width="70%"
        styles={{
          body: {
            maxHeight: "65vh",
            overflowY: "auto",
            overflowX: "hidden",
          },
        }}
        title={
          <Flex align="flex-start" justify="space-between">
            <strong>Select Media</strong>
            {Object.keys(alert).length > 0 ? <Alert {...alert} /> : null}
            <Flex align="center" gap={8}>
              <Upload
                {...UploadUtils.buttonTypeProps}
                multiple={true}
                accept="image/*"
                onChange={({ file }) => {
                  if (file?.status === "done") {
                    getMedia({ page: 1 });
                  }
                }}
              >
                <Button icon={<RiUploadCloud2Line />}>Upload</Button>
              </Upload>
              <Input
                placeholder="Search"
                style={{ width: 200 }}
                prefix={<RiSearchLine />}
                onChange={(e) => setSearch(e.target.value)}
                onPressEnter={() => {
                  getMedia({
                    page: 1,
                    search: search,
                  });
                }}
              />
              <Button
                icon={<RiCloseFill fontSize={20} />}
                type="text"
                onClick={() => setIsModalOpen(false)}
              />
            </Flex>
          </Flex>
        }
      >
        {loading === true ? (
          <Row gutter={[8, 8]}>
            {Array.from({ length: 24 })
              .fill(0)
              .map((_, index) => {
                return (
                  <Col span={3} id={index} key={`skeleton-${index}`}>
                    <div className="media-wrapper">
                      <Skeleton.Image active />
                    </div>
                  </Col>
                );
              })}
            <Col span={24}>
              <Flex align="center" justify="center" vertical gap={8}>
                <Skeleton.Input />
                <Skeleton.Button />
              </Flex>
            </Col>
          </Row>
        ) : media.total === 0 ? (
          <Empty />
        ) : (
          <Image.PreviewGroup preview={false}>
            <Row gutter={[10, 10]} style={{ margin: 0, paddingBlock: 10 }}>
              {media?.data?.map((mediaItem) => {
                return (
                  <Col span={3} id={mediaItem.uid} key={mediaItem.uid}>
                    <div
                      className={[
                        "media-wrapper",
                        value?.map((i) => i.uid).includes(mediaItem.uid)
                          ? "media-wrapper-selected"
                          : "",
                      ].join(" ")}
                      style={{ position: "relative" }}
                    >
                      <Checkbox
                        checked={value
                          ?.map((i) => i.uid)
                          .includes(mediaItem.uid)}
                        onChange={(e) => {
                          const isSelected = e.target.checked;
                          if (!isSelected) {
                            let updatedSelectedObj = [...value].filter(
                              (imgObj) => imgObj.uid !== mediaItem.uid,
                            );
                            setAlert({});
                            onChange([...updatedSelectedObj]);
                          } else {
                            if (value.length >= max) {
                              setAlert({
                                type: "warning",
                                message: `You can select maximum ${max} items`,
                                showIcon: true,
                              });
                            } else {
                              setAlert({});
                              onChange([...value, mediaItem]);
                            }
                          }
                        }}
                        style={{
                          position: "absolute",
                          top: 8,
                          left: 8,
                          zIndex: 11,
                        }}
                      />
                      <div
                        onClick={(e) => {
                          // Prevent closing if they clicked the preview mask
                          if (e.target.closest(".ant-image-mask")) return;

                          if (
                            value?.map((i) => i.uid).includes(mediaItem.uid)
                          ) {
                            setIsModalOpen(false); // Already selected, just close
                          } else {
                            if (value.length >= max) {
                              setAlert({
                                type: "warning",
                                message: `You can select maximum ${max} items`,
                                showIcon: true,
                              });
                            } else {
                              setAlert({});
                              onChange([...value, mediaItem]);
                              setIsModalOpen(false);
                            }
                          }
                        }}
                        style={{ width: "100%", height: "100%" }}
                      >
                        <Image
                          src={mediaItem.url}
                          preview={{
                            src: mediaItem.thumbnail,
                          }}
                        />
                      </div>
                      <div
                        style={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          width: "100%",
                          background: "rgba(0,0,0,0.6)",
                          color: "#fff",
                          fontSize: 10,
                          textAlign: "center",
                          padding: "2px 4px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          zIndex: 10,
                          borderBottomLeftRadius: 16,
                          borderBottomRightRadius: 16,
                          pointerEvents: "none",
                        }}
                      >
                        {mediaItem.name}
                      </div>
                    </div>
                  </Col>
                );
              })}
              <Col span={24}>
                <Flex align="center" justify="center" vertical gap={8} style={{ marginTop: 16, marginBottom: 16 }}>
                  <Pagination
                    current={media.current_page}
                    total={media.total}
                    pageSize={media.per_page}
                    onChange={(page) => getMedia({ page: page, search: search })}
                    showSizeChanger={false}
                  />
                </Flex>
              </Col>
            </Row>
          </Image.PreviewGroup>
        )}
      </Modal>
    </>
  );
}
